/**
 * Single source of truth for "will this report anchor actually render in the
 * review-and-sign body?"
 *
 * Both the treatment-plan body components (which decide what to render) and the
 * QA suggestion gate/sidebar (which decide what to count / display) use these
 * predicates. Keeping display and count on the *same* predicate is what stops
 * orphaned suggestions — active suggestions anchored to a goal/target that
 * never renders — from silently blocking report completion. See PROA-2122.
 *
 * Body visibility rules (mirrored here):
 *  - Goals render when `!excludeFromReport`. The reassessment goals query is
 *    already scoped to `in_use=True` on the backend, so goal `inUse` is not
 *    fetched and must NOT be part of the goal predicate.
 *  - Targets render when `inUse && !excludeFromReport`.
 */

export interface GoalVisibilityFields {
  excludeFromReport?: boolean | null;
}

export interface TargetVisibilityFields {
  inUse?: boolean | null;
  excludeFromReport?: boolean | null;
}

/** A goal renders in the report body when it isn't excluded from the report. */
export const isReportGoalVisible = (goal: GoalVisibilityFields | null | undefined): boolean =>
  !!goal && !goal.excludeFromReport;

/** A target renders in the report body when it's in use and not excluded. */
export const isReportTargetVisible = (target: TargetVisibilityFields | null | undefined): boolean =>
  !!target && !!target.inUse && !target.excludeFromReport;

/** Minimal suggestion shape needed to decide whether its anchor renders. */
export interface ResolvableSuggestionFields {
  reportSectionField?: { id: string } | null;
  reportGoal?: (GoalVisibilityFields & { id: string }) | null;
  reportTarget?: (TargetVisibilityFields & { id: string }) | null;
}

/**
 * A suggestion is "resolvable" when its anchor actually renders in the review-and-sign body —
 * i.e. the clinician can see and resolve it. Suggestions anchored to a hidden goal/target are NOT
 * resolvable, so they must neither block completion nor appear in the suggestions sidebar.
 *
 * `isActive`-based filtering (superseded suggestions) is no longer this function's job — the
 * backend's unfiltered getReportReviewItems resolver already excludes inactive QA-scan rows
 * entirely, so nothing reaching this function can be inactive.
 */
export const isSuggestionResolvable = (suggestion: ResolvableSuggestionFields | null | undefined): boolean => {
  if (!suggestion) return false;

  // Target-scoped: the target must render (in use, not excluded).
  // We intentionally do NOT also check the parent goal's excludeFromReport:
  // that would require traversing reportTarget -> reportGoal in the suggestions
  // query (an extra join we're avoiding). A target under an excluded goal is a
  // rare orphan we accept rather than take on that query cost.
  if (suggestion.reportTarget) {
    return isReportTargetVisible(suggestion.reportTarget);
  }

  // Goal-scoped: the goal must render.
  if (suggestion.reportGoal) {
    return isReportGoalVisible(suggestion.reportGoal);
  }

  // Section-field-scoped (or unanchored): section fields always render.
  return true;
};

/** A review item is resolved once its status says so or the server stamped a resolution time. */
export const isReviewItemResolved = (item: { status?: string | null; resolvedAt?: unknown }): boolean =>
  item.status === "resolved" || item.status === "applied" || item.resolvedAt != null;
